import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import { chooseLocation } from 'app/lib/scheduleLocation'
import type { PmsGate, PmsLocation } from 'app/types/pms'
import { PMS_SUPPORT, pmsGate } from 'lib/pmsCapabilities'

/**
 * Set one search param, and drop `wId` on the way out. WorkspaceGate stamps
 * `wId` with `history.replaceState`, which React Router never sees, so writing
 * its stale copy back costs a `switchWorkspace` and a full page reload.
 */
function withParam(current: URLSearchParams, key: string, value: string): URLSearchParams {
  const next = new URLSearchParams(current)
  next.delete(`wId`)
  next.set(key, value)
  return next
}

export type SchedulePage = {
  locations: { id: string; name: string }[]
  /** Locations whose PMS can't draw a schedule, named so they aren't silently missing. */
  excludedLocations: string[]
  pmsGate: PmsGate
  locationId?: string
  locationName?: string
  loadingLocations: boolean
  /** Several practices and none chosen yet, so the board asks before it draws. */
  asking: boolean
  onLocation: (id: string) => void
}

/**
 * Which practice the board is drawn for: any location whose PMS syncs room schedules. Product
 * filters here cost real practices the whole page twice, and TRK-5607 removed the matching gate
 * on the API.
 */
export function useSchedulePage(): SchedulePage {
  const [searchParams, setSearchParams] = useSearchParams()
  const { locationOptions, isLoading } = useLocationIntegrationStatus()

  const { locations, excludedLocations, gate } = useMemo(() => {
    const all = locationOptions.map((option) => ({
      id: String(option.value),
      name: option.name ?? `Location`,
      pms: (option.meta?.pms ?? {}) as PmsLocation,
    }))
    const supported = all.filter((location) => PMS_SUPPORT.scheduleBoard(location.pms))
    return {
      locations: supported.map(({ id, name }) => ({ id, name })),
      excludedLocations: all.filter((location) => !supported.includes(location)).map((location) => location.name),
      gate: pmsGate(
        all.map((location) => location.pms),
        `scheduleBoard`,
      ),
    }
  }, [locationOptions])
  // `locations[0]` used to be the answer, which is how SMC's board opened on an
  // E2E seed fixture rather than on Hamblin Family Dentistry.
  const choice = chooseLocation(locations, searchParams.get(`locationId`))
  const locationId = choice.kind === `draw` ? choice.locationId : undefined
  const locationName = locations.find((location) => location.id === locationId)?.name

  const onLocation = useCallback(
    (id: string) => {
      setSearchParams(withParam(searchParams, `locationId`, id), { replace: true })
    },
    [searchParams, setSearchParams],
  )

  return {
    locations,
    excludedLocations,
    pmsGate: gate,
    locationId,
    locationName,
    loadingLocations: isLoading,
    // Gated on `isLoading`: every page load starts with no locations, and a
    // question asked in that moment would flash up on the way to its answer.
    asking: !isLoading && choice.kind === `ask`,
    onLocation,
  }
}
